/**
 * Default page templates with all editable content
 * These serve as the initial content for each page if nothing is saved in the database
 */

export const pageTemplates = {
  "forside": {
    slug: "forside",
    name: "Forside",
    sections: [
      { 
        type: "hero", 
        title: "Find dit næste kursus", 
        description: "Udforsk vores udvalg af kurser og tilmeld dig i dag. Ingen login påkrævet." 
      },
      {
        type: "config",
        searchPlaceholder: "Søg efter kurser...",
        calendarButton: "Se kursuskalender"
      }
    ]
  },
  
  "om-os": {
    slug: "om-os",
    name: "Om os",
    sections: [
      { 
        type: "hero", 
        title: "Velkommen til Votra", 
        description: "Vi er dedikeret til at levere kvalitetsuddannelse og kompetenceudvikling inden for vognmandsbranchen." 
      },
      { 
        type: "text", 
        key: "mission",
        title: "Vores mission", 
        content: "Vi arbejder for at gøre kvalitetsuddannelse tilgængelig for alle. Vores kurser er designet til at give praktiske færdigheder, der kan anvendes direkte i arbejdslivet." 
      },
      { 
        type: "stats", 
        items: [
          { label: "Kursushold udbudt", value: "50+" },
          { label: "Års erfaring", value: "10+" },
          { label: "Tilfredshed", value: "98%" }
        ]
      },
      { 
        type: "values", 
        items: [
          { icon: "target", title: "Kvalitet", description: "Vi leverer undervisning af højeste kvalitet med erfarne instruktører." },
          { icon: "users", title: "Fællesskab", description: "Vi skaber et inkluderende læringsmiljø for alle." },
          { icon: "award", title: "Certificering", description: "Alle vores kurser giver officielt anerkendte certifikater." }
        ]
      }
    ]
  },

  "ledige": {
    slug: "ledige",
    name: "Ledige",
    sections: [
      { 
        type: "hero", 
        title: "Styrk dine kompetencer som ledig", 
        description: "Som ledig har du mulighed for at deltage i kurser med 6-ugers selvvalgt uddannelse. Vi hjælper dig med at komme i gang." 
      },
      { 
        type: "benefits",
        title: "Dine fordele som ledig",
        items: [
          "Gratis kurser med 6-ugers selvvalgt uddannelse",
          "Bevar din dagpengeret under uddannelsen",
          "Få anerkendte certifikater",
          "Øg dine chancer for at komme i job",
          "Fleksible kursustidspunkter"
        ]
      },
      { 
        type: "steps",
        title: "Sådan kommer du i gang",
        items: [
          { number: "1", title: "Kontakt dit jobcenter", description: "Tal med din sagsbehandler om 6-ugers selvvalgt uddannelse" },
          { number: "2", title: "Vælg et kursus", description: "Find et kursus der passer til dine mål" },
          { number: "3", title: "Tilmeld dig", description: "Vi klarer resten og sender bekræftelse" }
        ]
      },
      { 
        type: "cta",
        title: "Klar til at komme i gang?",
        description: "Se vores kursusudbud og find det rette kursus til dig.",
        buttonText: "Se alle kurser"
      }
    ]
  },

  "jobcentre": {
    slug: "jobcentre",
    name: "Jobcentre & A-kasser",
    sections: [
      { 
        type: "hero", 
        title: "Samarbejde med jobcentre & A-kasser", 
        description: "Vi tilbyder skræddersyede løsninger til jobcentre og A-kasser. Få dine ledige hurtigt i gang med kompetencegivende kurser." 
      },
      { 
        type: "services",
        title: "Vores tilbud til jer",
        items: [
          "Særlige priser for jobcentre og A-kasser",
          "Fleksible holdstørrelser og datoer",
          "Hurtig opstart - ofte inden for 2 uger",
          "Opfølgning og rapportering",
          "Dedikeret kontaktperson",
          "Skræddersyede forløb efter behov"
        ]
      },
      { 
        type: "pricing",
        title: "Priser for Jobcentre/A-kasser",
        description: "Vi tilbyder særlige priser til jobcentre og A-kasser. Kontakt os for at få et tilbud tilpasset jeres behov.",
        note: "Priserne inkluderer undervisning, materialer og certificering."
      },
      { 
        type: "contact",
        title: "Kontakt os",
        description: "Er du sagsbehandler eller ansvarlig for opkvalificering? Kontakt os for at høre mere om vores tilbud.",
        phone: "+45 55 29 28 28",
        email: "kundeservice@votra.dk"
      }
    ]
  },

  "partner": {
    slug: "partner",
    name: "Partnerprogram",
    sections: [
      { 
        type: "hero", 
        badge: "Bliv partner",
        title: "Partnerprogram", 
        description: "Bliv en del af vores netværk og hjælp flere med at finde de rette kurser. Vi tilbyder attraktive samarbejdsmodeller." 
      },
      { 
        type: "benefits",
        title: "Fordele ved partnerskab",
        items: [
          { title: "Provision", description: "Attraktiv provision på alle henvisninger" },
          { title: "Markedsføringsmateriale", description: "Gratis adgang til kampagnemateriale" },
          { title: "Dedikeret support", description: "En fast kontaktperson til jeres behov" },
          { title: "Rapportering", description: "Løbende indsigt i konverteringer" },
          { title: "Fleksibilitet", description: "Tilpas samarbejdet efter jeres model" },
          { title: "Netværk", description: "Adgang til vores partnernetværk" }
        ]
      },
      { 
        type: "partnerTypes",
        items: [
          { title: "Rekrutteringsbureauer", description: "Tilbyd kurser til jeres kandidater og styrk deres profil." },
          { title: "Uddannelsesinstitutioner", description: "Udvid jeres portefølje med vores kurser." },
          { title: "Virksomheder", description: "Opkvalificer jeres medarbejdere gennem os." }
        ]
      },
      { 
        type: "cta",
        title: "Interesseret i et partnerskab?",
        description: "Kontakt os for at høre mere om mulighederne.",
        buttonText: "Kontakt os"
      }
    ]
  },

  "kontakt": {
    slug: "kontakt",
    name: "Kontakt os",
    sections: [
      { 
        type: "hero", 
        title: "Kontakt os", 
        description: "Vi er her for at hjælpe dig. Kontakt os via telefon, email eller udfyld formularen." 
      },
      { 
        type: "contactInfo",
        phone: { label: "Telefon", value: "+45 55 29 28 28", hours: "Hverdage: 07:30 - 17:30" },
        email: { label: "Email", value: "kundeservice@votra.dk", note: "Vi svarer hurtigst muligt, typisk inden for 24 timer" },
        address: { label: "Adresse", line1: "Sct. Hansgade 9, 2. sal", line2: "4100 Ringsted" },
        hours: { label: "Åbningstider", weekdays: "Hverdage: 07:30 - 23:30", weekend: "Weekend: 08:00 - 23:30" }
      },
      { 
        type: "form",
        title: "Send os en besked",
        fields: {
          name: { label: "Navn", placeholder: "Dit navn" },
          email: { label: "Email", placeholder: "din@email.dk" },
          subject: { label: "Emne", placeholder: "Hvad handler det om?" },
          message: { label: "Besked", placeholder: "Skriv din besked her..." }
        },
        buttonText: "Send besked"
      }
    ]
  },

  "nyttige-links": {
    slug: "nyttige-links",
    name: "Nyttige links",
    sections: [
      { 
        type: "hero", 
        title: "Nyttige links", 
        description: "Her finder du en samling af nyttige links relateret til uddannelse, jobsøgning og A-kasser." 
      },
      { 
        type: "linkCategories",
        categories: [
          {
            title: "Offentlige myndigheder",
            items: [
              { name: "Borger.dk", url: "https://www.borger.dk", description: "Danmarks officielle selvbetjeningsportal" },
              { name: "Jobnet.dk", url: "https://www.jobnet.dk", description: "Jobsøgning og ledighed" },
              { name: "SKAT", url: "https://www.skat.dk", description: "Skatterelaterede spørgsmål" }
            ]
          },
          {
            title: "Uddannelse",
            items: [
              { name: "AMU-kurser.dk", url: "https://www.amu-kurser.dk", description: "Find AMU-kurser" },
              { name: "UddannelsesGuiden", url: "https://www.ug.dk", description: "Information om uddannelser" },
              { name: "STAR", url: "https://www.star.dk", description: "Styrelsen for Arbejdsmarked og Rekruttering" }
            ]
          },
          {
            title: "A-kasser",
            items: [
              { name: "3F", url: "https://www.3f.dk", description: "A-kasse for faglærte" },
              { name: "HK", url: "https://www.hk.dk", description: "A-kasse for handel og kontor" },
              { name: "Akademikerne", url: "https://www.akademikerne.dk", description: "A-kasse for akademikere" }
            ]
          }
        ]
      }
    ]
  },

  "persondata": {
    slug: "persondata",
    name: "Persondata og cookies",
    sections: [
      { 
        type: "hero", 
        title: "Persondata og cookies", 
        description: "Læs om hvordan vi håndterer dine personoplysninger" 
      },
      { 
        type: "textSections",
        items: [
          { title: "1. Dataansvarlig", content: "Votra Danmark ApS, CVR 36486899, Sct. Hansgade 9, 2. sal, 4100 Ringsted, er dataansvarlig for behandlingen af de personoplysninger, vi har modtaget om dig. Du finder vores kontaktoplysninger nedenfor." },
          { title: "2. Hvilke oplysninger indsamler vi?", content: "Vi indsamler og behandler følgende personoplysninger om dig:\n• Navn og kontaktoplysninger\n• Fødselsdato\n• Kursushistorik og certifikater\n• Betalingsoplysninger" },
          { title: "3. Formål med behandlingen", content: "Vi bruger dine oplysninger til at administrere din tilmelding til kurser, udstede certifikater og kommunikere med dig om relevante kurser." },
          { title: "4. Cookies", content: "Vi bruger cookies til at forbedre din oplevelse på vores hjemmeside. Du kan til enhver tid ændre dine cookie-indstillinger i din browser." },
          { title: "5. Dine rettigheder", content: "Du har ret til at få indsigt i dine oplysninger, få dem rettet eller slettet. Kontakt os på sikker@votra.dk for at udøve dine rettigheder." },
          { title: "6. Kontakt", content: "Ved spørgsmål til vores behandling af dine personoplysninger kan du kontakte os på:\nEmail: kundeservice@votra.dk\nTelefon: +45 55 29 28 28" }
        ]
      }
    ]
  },

  "kursusbetingelser": {
    slug: "kursusbetingelser",
    name: "Kursusbetingelser",
    sections: [
      { 
        type: "hero", 
        title: "Kursusbetingelser", 
        description: "Læs vores vilkår og betingelser for deltagelse i kurser" 
      },
      { 
        type: "textSections",
        items: [
          { title: "1. Tilmelding", content: "Ved tilmelding til et kursus accepterer du disse betingelser. Tilmeldingen er bindende, når du har modtaget en bekræftelse fra os." },
          { title: "2. Betaling", content: "Betaling skal ske senest 14 dage før kursusstart, medmindre andet er aftalt. Ved tilmelding mindre end 14 dage før kursusstart skal betaling ske straks." },
          { title: "3. Afmelding", content: "Afmelding skal ske skriftligt. Ved afmelding mere end 14 dage før kursusstart refunderes det fulde beløb. Ved afmelding mindre end 14 dage før kursusstart refunderes 50% af beløbet." },
          { title: "4. Aflysning", content: "Vi forbeholder os ret til at aflyse kurser ved for få tilmeldinger. I så fald refunderes det fulde beløb, eller du kan vælge at blive flyttet til en anden dato." },
          { title: "5. Fremmøde", content: "For at opnå certifikat kræves minimum 80% fremmøde samt bestået afsluttende prøve, hvor relevant." },
          { title: "6. Ansvar", content: "Vi er ikke ansvarlige for personlige ejendele. Deltagere skal selv sørge for passende forsikring." },
          { title: "7. Kontakt", content: "Ved spørgsmål til betingelserne kan du kontakte os på kundeservice@votra.dk eller +45 55 29 28 28." }
        ]
      }
    ]
  },

  "business": {
    slug: "business",
    name: "Votra Business",
    sections: [
      {
        type: "hero",
        title: "Vi får dig i drift, klar til indtjening fra dag ét",
        description: "Vi hjælper dig fra idé til fuld drift. CVR, leasing, kontrakter og compliance samlet ét sted. Vi har allerede hjulpet flere vognmænd i gang."
      },
      {
        type: "text",
        key: "trust",
        title: "Bygget på erfaring fra transportbranchen",
        content: "Certificeret ADR rådgivning · Stærke samarbejdspartnere · Fokus på drift og indtjening"
      },
      {
        type: "text",
        key: "final-cta",
        title: "Klar til at starte din vognmandsforretning?",
        content: "Klik på 'Book gratis opstartsmøde' for at komme i gang."
      }
    ]
  }
};

/**
 * Get template for a specific page
 * @param {string} slug - Page slug
 * @returns {Object} Page template
 */
export const getPageTemplate = (slug) => {
  return pageTemplates[slug] || null;
};

/**
 * Get section from page data by type
 * @param {Array} sections - Sections array
 * @param {string} type - Section type
 * @returns {Object|null} Found section
 */
export const getSection = (sections, type) => {
  if (!sections || !Array.isArray(sections)) return null;
  return sections.find(s => s.type === type) || null;
};
